// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { localize } from '@ta/common.i18n';
import classNames from '@ta/common.classnames';
import { LoadingBubbles } from '@ta/styleguide.loading';
import Button from '@ta/styleguide.button';
import styles from './ConfirmationView.less';

type ExternalProps = {|
  /** The React node to display for the description */
  descriptionContent: React.Node,
  /** The React node to display for the submit button */
  submitContent: React.Node,
  /** A callback executed in the event of submission */
  onSubmit?: () => mixed,
  /** A callback executed when the cancel button is clicked */
  onCancel?: () => mixed,
  /** If true, the modal will look like it is submitting */
  isSubmitting?: boolean,
  /** If true, the modal will render an error */
  isError?: boolean,
|};

type Props = {|
  /** Props visible to consumers of the default exported component */
  ...ExternalProps,
  /** Internal: Whether the current viewport category is that of a mobile device. Provided by redux store */
  isMobile: boolean,
  /** Internal: Whether the user agent is IE11. IE11 does not properly infer width of nested flex containers, so special treatment is needed. Provided by redux store */
  isIE11: boolean,
|};

/** Exported for testing purposes */
class ConfirmationView extends React.Component<Props> {
  static defaultProps = {
    submitContent: 'Submit',
    onCancel: undefined,
    onSubmit: undefined,
    isSubmitting: false,
    isError: false,
  };

  constructor(props: Props) {
    super(props);
    this.submitBtnRect = props.isMobile ? { width: 180, height: 36 } : { width: 81, height: 36 };
  }

  componentDidMount() {
    this.updateBtnRect();
  }

  componentDidUpdate(prevProps: Props) {
    // In the off-chance that the modal was somehow initialized with isSubmitting prop set to true.
    // Grab the rect the next time around
    if (prevProps.isSubmitting && !this.props.isSubmitting) {
      this.updateBtnRect();
    }
  }

  // Bind and re-apply to sanitize arguments
  onSubmit = () => {
    if (typeof this.props.onSubmit === 'function') {
      this.props.onSubmit();
    }
  };

  // ditto
  onCancel = () => {
    if (typeof this.props.onCancel === 'function') {
      this.props.onCancel();
    }
  };

  updateBtnRect = () => {
    // Grabs the current rectangle of the submitBtn to ensure we have a seamless transition between Submit -> Loading Spinner

    // Only update width for now, as that can change from locale to locale. Height _could_ change,
    // but because the computed height for a button isn't correct, we can't account for it
    const { current } = this.submitBtnRef;
    const { height } = this.submitBtnRect;
    if (current) {
      this.submitBtnRect = {
        width: current.offsetWidth,
        height,
      };
    }
  };

  /** A ref to the submit button */
  submitBtnRef: React.ElementRef<*> = React.createRef();
  /** The memorized rectangle. Defaults to a reasonable value in constructor */
  submitBtnRect: { width: number, height: number };

  render() {
    const { isSubmitting, descriptionContent, submitContent, isMobile, isIE11 } = this.props;
    const { width, height } = this.submitBtnRect;
    return (
      <div
        className={classNames(styles.container, {
          [styles.submitting]: isSubmitting,
          [styles.IE11]: isIE11,
        })}
      >
        {descriptionContent}
        <div className={styles.submit}>
          <span className={classNames(styles.button_flex_wrap, styles.button_right_gap)}>
            <Button
              type="secondary"
              size={isMobile ? 'fullwidth' : 'default'}
              onClick={this.onCancel}
            >
              {/** Cancel */}
              {localize('social_block_modal_cancel')}
            </Button>
          </span>
          {isSubmitting ? (
            <div
              style={{ width, height }}
              className={classNames(styles.loader, styles.button_left_gap)}
            >
              <LoadingBubbles color="dark" />
            </div>
          ) : (
            <span className={classNames(styles.button_flex_wrap, styles.button_left_gap)}>
              <span ref={this.submitBtnRef}>
                <Button
                  type="primary"
                  size={isMobile ? 'fullwidth' : 'default'}
                  onClick={this.onSubmit}
                >
                  {submitContent}
                </Button>
              </span>
            </span>
          )}
        </div>
      </div>
    );
  }
}

export { ConfirmationView as BaseConfirmationView };

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  isMobile: state.meta.device.viewportCategory !== 'DESKTOP',
  isIE11:
    state.meta.device.browser.family === 'IE' && state.meta.device.browser.majorVersion === 11,
}))(ConfirmationView);
